import React, { memo } from "react";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import { Flex } from "@UI/Components/Flex/Flex";
import { Icon } from "@UI/Components/Icon/Icon";
import { Text } from "@UI/Components/Text/Text";
import { Image } from "@UI/Components/Image/Image";
import { UserIcon } from "@UI/Components/Icons/UserIcon";
import { IExtendedMetric } from "@modules/ServiceProviders/types/serviceProvider.types";
import { MetricCard } from "../MetricCard";
import messages from "../messages";

interface IClientsClosedCard {
  metric: IExtendedMetric | undefined;
  onClick?: () => void;
}

export const ClientsClosedCard = memo(({ metric, onClick }: IClientsClosedCard) => {
  const value = Number(metric?.value ?? 0);
  const threshold = Number(metric?.threshold) || 5;
  const reached =
    metric !== undefined && metric.value !== null && value >= threshold;
  const clientsData = metric?.computationDetails?.clientsData ?? [];

  return (
    <MetricCard
      title={<FormattedMessage {...messages.clientsClosedTitle} />}
      onClick={onClick}
      description={
        <FormattedMessage
          {...messages.clientsClosedDescription}
          values={{ value: threshold }}
        />
      }
      state={reached ? "reached" : "not-reached"}
    >
      <Flex gap={2} flexWrap="wrap">
        {Array.from({ length: threshold }).map((_, i) => {
          const client = clientsData[i];
          if (!client) {
            return (
              <Flex
                key={i}
                w="40px"
                h="40px"
                rounded="full"
                border="1.5px dashed"
                borderColor="gray.300"
                align="center"
                justify="center"
                flexShrink={0}
                shadow="rt-drop-sm"
              >
                <Icon as={UserIcon} color="gray.300" boxSize={4} />
              </Flex>
            );
          }
          const hasPhoto =
            !!client.profilePictureUrl &&
            client.profilePictureUrl !== "no_avatar.jpg";
          if (hasPhoto) {
            return (
              <Flex
                key={i}
                w="40px"
                h="40px"
                rounded="full"
                overflow="hidden"
                flexShrink={0}
                shadow="rt-drop-sm"
              >
                <Image
                  src={client.profilePictureUrl}
                  alt={client.initials}
                  w="full"
                  h="full"
                  objectFit="cover"
                />
              </Flex>
            );
          }
          return (
            <Flex
              key={i}
              w="40px"
              h="40px"
              rounded="full"
              border="2px solid"
              borderColor="seaGreen.500"
              bg="white"
              align="center"
              justify="center"
              flexShrink={0}
              shadow="rt-drop-sm"
            >
              <Text
                fontSize="xs"
                fontWeight="700"
                color="seaGreen.500"
                lineHeight="1"
              >
                {client.initials}
              </Text>
            </Flex>
          );
        })}
      </Flex>
    </MetricCard>
  );
});

ClientsClosedCard.displayName = "ClientsClosedCard";
